import { Navigate } from "react-router-dom";
import { ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const ADMIN_ROLES = ["admin", "super_admin"];

// Gates the admin portal: requires an authenticated Supabase user whose profile
// role is admin/super_admin. Non-admins get a clean Access Denied screen.
export const AdminGuard = ({ children }) => {
  const { loading, configured, user, role } = useAuth();

  if (!configured) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] p-6 text-center">
        <div>
          <ShieldAlert className="mx-auto h-10 w-10 text-[#1E3A8A]" />
          <h1 className="mt-4 text-xl font-semibold text-slate-900">Admin unavailable</h1>
          <p className="mt-2 text-slate-600">Supabase is not configured.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA]" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login?redirect=/admin" replace />;

  if (!ADMIN_ROLES.includes(role)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] p-6" data-testid="admin-denied">
        <div className="max-w-md rounded-3xl border border-border bg-white p-8 text-center shadow-[var(--shadow-sm)]">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C0392B]/10 text-[#C0392B]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <h1 className="mt-5 text-xl font-semibold text-slate-900">Access denied</h1>
          <p className="mt-2 text-slate-600">You need administrator privileges to access this area.</p>
          <Button asChild className="mt-6 rounded-xl bg-[#1E3A8A] hover:bg-[#172D6B]">
            <a href="/">Back to ABAY360</a>
          </Button>
        </div>
      </div>
    );
  }

  return children;
};

export default AdminGuard;
